import { Download, LoaderCircle, Play, Save, Share2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  Button,
  DateRangePicker,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  buildDateRangePickerPresets,
  toast,
  type DateRangePickerValue,
} from '@shigo-ui/ui';

import {
  resolveOwnedDashboardContext,
  ShigoDashboardContext,
} from '../../../../auth/dashboardDestination';
import { analyticsMaxDate } from '../../../../../lib/analyticsDateRange';
import { copyToClipboard } from '../../../../../lib/clipboard';
import { showRequestError } from '../../../../../lib/requestErrorToast';
import { NetworkAvailableFeature } from '../../../../auth/networkFeatures';
import { useNetworkFeatures } from '../../../../auth/useNetworkFeatures';
import { ShigoPermission } from '../../../../auth/permissions';
import { useShigoPermissions } from '../../../../auth/useShigoPermissions';
import { dashboardT } from '../../../../../lib/messages';
import { useShigoSession } from '../../../../../lib/useShigoSession';
import { isReportSelectionCompatible } from '../../compatibility';
import { canExportReport, exportReportCsv } from '../../csvExport';
import { useCreateReportShareMutation, useReportQuery, ReportQueryKind } from '../../hooks';
import type { ReportExplorerConfiguration } from '../../model';
import {
  datePresetKeyByReportDateRange,
  normalizeReportExplorerRange,
} from '../../reportConfiguration';
import { useSavedReportMutations } from '../../../savedReports/hooks';
import { savedReportSharedUserIds, type SavedReportConfig } from '../../../savedReports/model';
import {
  buildChartReportRequest,
  chartDimensionsForGranularity,
  buildTimeShiftReportRequest,
  buildTableReportRequest,
  createDefaultReportExplorerConfiguration,
  createDefaultAgencyReportExplorerConfiguration,
  createDefaultPublisherReportExplorerConfiguration,
  reportDateRangeSupportsHour,
} from '../../reportRequest';
import {
  ReportDateRangeType,
  ReportExplorerScope,
  ReportTimeGranularity,
  type ReportDimension,
} from '../../reportEnums';
import {
  publisherReportFilterFields,
  reportDimensionFilterField,
  serializeReportExplorerFilterValueLabels,
} from '../../reportFilters';
import {
  dimensionSelectionSections,
  metricSelectionSections,
  sanitizeReportExplorerConfiguration,
} from '../../selectionSections';
import { ReportChartPanel } from '../ReportChartPanel';
import { ReportDimensionFilter } from '../ReportDimensionFilter';
import { ReportFieldRail } from '../ReportFieldRail';
import { ReportResultsTable } from '../ReportResultsTable';
import { renderChartMetricsDialog } from './reportSelectionDialog';
import { reportFieldRailRules } from './reportFieldRailRules';
import { reportPayload, sameReportRequest, withReportDates } from './reportExplorerPageModel';
import { SaveReportDialog } from '../SaveReportDialog';

const datePresets = buildDateRangePickerPresets(
  Object.values(ReportDateRangeType).map((preset) => datePresetKeyByReportDateRange[preset]),
  { endPeriodsYesterday: true },
);
const allNetworkFeatures = Object.values(NetworkAvailableFeature);

interface ReportExplorerPageProps {
  initialConfiguration?: ReportExplorerConfiguration | undefined;
  initialSavedReport?: SavedReportConfig | undefined;
  scope?: ReportExplorerScope | undefined;
  publisherId?: string | undefined;
  agencyId?: string | undefined;
  /** Saved report picker supplied by routes that can load saved reports. */
  savedReportPicker?: ReactNode;
}

export function ReportExplorerPage({
  initialConfiguration,
  initialSavedReport,
  scope = ReportExplorerScope.Standard,
  publisherId,
  agencyId,
  savedReportPicker,
}: ReportExplorerPageProps = {}) {
  const { session } = useShigoSession();
  const agencyScope = scope === ReportExplorerScope.Agency;
  const publisherScope = scope === ReportExplorerScope.Publisher;
  let dashboardContext = ShigoDashboardContext.Main;
  if (agencyScope) dashboardContext = ShigoDashboardContext.Agency;
  else if (publisherScope) dashboardContext = ShigoDashboardContext.Publisher;
  else if (
    session &&
    resolveOwnedDashboardContext(session.user) === ShigoDashboardContext.SuperAdmin
  ) {
    dashboardContext = ShigoDashboardContext.SuperAdmin;
  }
  const { features } = useNetworkFeatures();
  const availableFeatures =
    dashboardContext === ShigoDashboardContext.SuperAdmin ? allNetworkFeatures : features;
  const defaultConfiguration = () => {
    if (agencyScope) return createDefaultAgencyReportExplorerConfiguration();
    if (publisherScope) return createDefaultPublisherReportExplorerConfiguration();
    return createDefaultReportExplorerConfiguration();
  };
  const [config, setConfig] = useState<ReportExplorerConfiguration>(() =>
    sanitizeReportExplorerConfiguration(
      normalizeReportExplorerRange(initialConfiguration ?? defaultConfiguration()),
      availableFeatures,
      dashboardContext,
    ),
  );
  const [chartMetricsOpen, setChartMetricsOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saved, setSaved] = useState<SavedReportConfig | null>(initialSavedReport ?? null);
  const { can } = useShigoPermissions();
  const canRun =
    agencyScope ||
    (publisherScope
      ? can(ShigoPermission.PublisherDashboardReportingRead)
      : can(ShigoPermission.ReportingReportBuilderRun));
  const canShare =
    !agencyScope &&
    (publisherScope
      ? can(ShigoPermission.PublisherDashboardReportingRead)
      : can(ShigoPermission.ReportingReportBuilderRead));
  const canSave =
    agencyScope || (!publisherScope && can(ShigoPermission.ReportingCustomReportsCreate));
  const tableRequest = buildTableReportRequest(config, publisherScope ? publisherId : undefined);
  const chartRequest = buildChartReportRequest(config, publisherScope ? publisherId : undefined);
  const timeShiftRequest = buildTimeShiftReportRequest(chartRequest, config.timeShiftDays);
  const tableComplete = tableRequest.dimensions.length > 0 && config.tableMetrics.length > 0;
  const chartComplete = config.chartMetrics.length > 0;
  const tableIncompatible =
    tableComplete &&
    !isReportSelectionCompatible(config.tableDimensions, config.tableMetrics, tableRequest.filters);
  const chartIncompatible =
    chartComplete &&
    !isReportSelectionCompatible(
      chartRequest.dimensions,
      config.chartMetrics,
      chartRequest.filters,
    );
  const tableValid = tableComplete && !tableIncompatible;
  const chartValid = chartComplete && !chartIncompatible;
  const publisherScopeValid = !publisherScope || Boolean(publisherId);
  // The table runs on an explicit Run (the initial configuration runs on load) and reruns
  // with new dates on a date change; the chart keeps following the selection.
  // The publisher route keeps this page mounted across publishers; a new publisher runs the
  // current selection instead of the previous publisher's report.
  const publisherContext = publisherScope ? publisherId : undefined;
  const [ranTable, setRanTable] = useState(() => ({
    publisherContext,
    request: tableValid ? tableRequest : null,
  }));
  const initialRunRequest = tableValid ? tableRequest : null;
  const ranTableRequest =
    ranTable.publisherContext === publisherContext ? ranTable.request : initialRunRequest;
  if (ranTable.publisherContext !== publisherContext) {
    setRanTable({ publisherContext, request: ranTableRequest });
  }
  const tableQuery = useReportQuery(
    ReportQueryKind.Table,
    ranTableRequest ?? tableRequest,
    canRun && ranTableRequest !== null && publisherScopeValid,
    scope,
    agencyId,
  );
  const tableStale = !sameReportRequest(ranTableRequest, tableRequest);
  // A changed selection can run while the previous request is still loading.
  const tablePending = !tableStale && tableQuery.isFetching;
  const runDisabled = !canRun || !tableValid || !publisherScopeValid || tablePending;
  const chartQuery = useReportQuery(
    ReportQueryKind.Chart,
    chartRequest,
    canRun && chartValid && publisherScopeValid,
    scope,
    agencyId,
  );
  const timeShiftQuery = useReportQuery(
    ReportQueryKind.ChartTimeShift,
    timeShiftRequest ?? chartRequest,
    canRun && chartValid && timeShiftRequest !== null && publisherScopeValid,
    scope,
    agencyId,
  );
  const mutations = useSavedReportMutations(agencyScope ? agencyId : undefined);
  const createShare = useCreateReportShareMutation();
  const hourGranularityAvailable = reportDateRangeSupportsHour(config.dateFrom, config.dateTo);
  const allowedFilterFields =
    publisherScope || agencyScope ? publisherReportFilterFields : undefined;
  const filterField = (dimension: ReportDimension) =>
    reportDimensionFilterField(dimension, availableFeatures, allowedFilterFields);
  const today = analyticsMaxDate();

  function updateDate(value: DateRangePickerValue) {
    const dateRange = Object.values(ReportDateRangeType).includes(
      value.preset as ReportDateRangeType,
    )
      ? (value.preset as ReportDateRangeType)
      : null;
    const supportsHour = reportDateRangeSupportsHour(value.dateFrom, value.dateTo);
    if (ranTableRequest) {
      setRanTable({
        publisherContext,
        request: withReportDates(ranTableRequest, {
          dateRange,
          dateFrom: value.dateFrom,
          dateTo: value.dateTo,
        }),
      });
    }
    setConfig((current) => ({
      ...current,
      dateFrom: value.dateFrom,
      dateTo: value.dateTo,
      dateRange,
      chartGranularity:
        supportsHour || current.chartGranularity !== ReportTimeGranularity.Hour
          ? current.chartGranularity
          : ReportTimeGranularity.Day,
    }));
  }

  function runTableReport() {
    if (tableStale) setRanTable({ publisherContext, request: tableRequest });
    else void tableQuery.refetch();
  }

  async function saveReport(name: string, sharedWith: string[]) {
    const payload = reportPayload(
      name,
      tableRequest,
      chartRequest,
      timeShiftRequest,
      config.reportFilters,
      sharedWith,
    );
    const result = saved
      ? await mutations.update.mutateAsync({ id: saved.id, payload })
      : await mutations.create.mutateAsync(payload);
    setSaved(result);
    setSaveOpen(false);
    toast.success(
      dashboardT(saved ? 'reportExplorer.save.updated' : 'reportExplorer.save.created'),
    );
  }

  async function shareReport() {
    if (agencyScope) return;
    let result: Awaited<ReturnType<typeof createShare.mutateAsync>>;
    try {
      result = await createShare.mutateAsync({
        report: tableRequest,
        settings: JSON.stringify({
          filterValueLabels: serializeReportExplorerFilterValueLabels(config.reportFilters),
        }),
        chartReports: [chartRequest, ...(timeShiftRequest ? [timeShiftRequest] : [])].map(
          (request) => JSON.stringify(request),
        ),
        ...(publisherScope && publisherId ? { publisherId } : {}),
      });
    } catch (error) {
      showRequestError(error, dashboardT('reportExplorer.share.error'));
      return;
    }

    const shareUrl = new URL(window.location.href);
    shareUrl.search = '';
    shareUrl.searchParams.set('u', result.urlHash);
    await copyToClipboard(shareUrl.toString(), {
      successMessage: dashboardT('reportExplorer.share.copied'),
      errorMessage: dashboardT('reportExplorer.share.copyError'),
    });
  }

  return (
    <main className="page-gutter min-w-0 flex-1 py-4 sm:py-6 lg:py-8">
      <div className="mx-auto flex w-full max-w-[1920px] min-w-0 flex-col gap-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 flex-wrap items-center gap-3">
            <h1 className="text-[26px] font-semibold">
              {dashboardT('nav.reporting.reportExplorer')}
            </h1>
            {savedReportPicker}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <DateRangePicker
              showDayNavigation
              label={dashboardT('reportExplorer.dateRange')}
              previousDayLabel={dashboardT('dashboard.date.previous')}
              nextDayLabel={dashboardT('dashboard.date.next')}
              presets={datePresets}
              value={{
                dateFrom: config.dateFrom,
                dateTo: config.dateTo,
                preset: config.dateRange ?? '',
              }}
              maxDate={today}
              onChange={updateDate}
            />
            <TooltipProvider delayDuration={150}>
              {canRun ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      aria-label={dashboardT('reportExplorer.export.action')}
                      disabled={
                        !ranTableRequest ||
                        tableQuery.isLoading ||
                        tableQuery.isError ||
                        !canExportReport(tableQuery.data)
                      }
                      onClick={() => {
                        if (tableQuery.data && ranTableRequest) {
                          exportReportCsv(ranTableRequest, tableQuery.data);
                        }
                      }}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{dashboardT('reportExplorer.export.action')}</TooltipContent>
                </Tooltip>
              ) : null}
              {canShare ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      aria-label={dashboardT('reportExplorer.share.action')}
                      disabled={
                        !tableValid || !chartValid || !publisherScopeValid || createShare.isPending
                      }
                      onClick={() => void shareReport()}
                    >
                      <Share2 className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{dashboardT('reportExplorer.share.action')}</TooltipContent>
                </Tooltip>
              ) : null}
              {canSave ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      aria-label={dashboardT('reportExplorer.save.action')}
                      disabled={!tableValid || !chartValid}
                      onClick={() => {
                        setSaveOpen(true);
                      }}
                    >
                      <Save className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{dashboardT('reportExplorer.save.action')}</TooltipContent>
                </Tooltip>
              ) : null}
            </TooltipProvider>
            {canRun ? (
              <Button type="button" disabled={runDisabled} onClick={runTableReport}>
                {tablePending ? (
                  <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Play className="h-4 w-4" aria-hidden="true" />
                )}
                {dashboardT('reportExplorer.run.action')}
              </Button>
            ) : null}
          </div>
        </div>
        {tableIncompatible || chartIncompatible ? (
          <p role="alert" className="text-destructive text-sm">
            {dashboardT('reportExplorer.selection.incompatible')}
          </p>
        ) : null}
        <div className="grid min-w-0 gap-6 xl:grid-cols-[minmax(260px,300px)_minmax(0,1fr)] xl:items-start">
          <ReportFieldRail
            dimensionSections={dimensionSelectionSections(availableFeatures, dashboardContext)}
            metricSections={metricSelectionSections(availableFeatures, false, dashboardContext)}
            dimensions={config.tableDimensions}
            metrics={config.tableMetrics}
            filters={config.reportFilters}
            disabled={!canRun}
            filterField={filterField}
            {...reportFieldRailRules(config, tableRequest.filters)}
            renderFilter={(field) => (
              <ReportDimensionFilter
                field={field}
                filters={config.reportFilters}
                disabled={!canRun}
                scope={scope}
                publisherId={publisherScope ? publisherId : undefined}
                onChange={(reportFilters) => {
                  setConfig((current) => ({ ...current, reportFilters }));
                }}
              />
            )}
            onDimensionsChange={(tableDimensions) => {
              // Filters stay applied (and removable in the rail) so the chart is untouched.
              setConfig((current) => ({ ...current, tableDimensions }));
            }}
            onMetricsChange={(tableMetrics) => {
              setConfig((current) => ({ ...current, tableMetrics }));
            }}
            onRemoveFilter={(field) => {
              setConfig((current) => ({
                ...current,
                reportFilters: current.reportFilters.filter((filter) => filter.field !== field),
              }));
            }}
          />
          {/* Matches the rail height on desktop so an empty table ends where the rail does. */}
          <div className="flex min-w-0 flex-col gap-6 xl:min-h-[calc(100dvh-2rem)]">
            <ReportChartPanel
              request={chartRequest}
              response={chartQuery.data}
              timeShiftResponse={timeShiftQuery.data}
              loading={chartQuery.isLoading}
              error={chartQuery.isError}
              timeShiftDays={config.timeShiftDays}
              timeShiftLoading={timeShiftQuery.isFetching}
              timeShiftError={timeShiftQuery.isError}
              granularity={config.chartGranularity}
              disabled={!canRun}
              hourDisabled={!hourGranularityAvailable}
              isGranularityDisabled={(granularity) =>
                (agencyScope &&
                  granularity !== ReportTimeGranularity.Day &&
                  granularity !== ReportTimeGranularity.Hour) ||
                !isReportSelectionCompatible(
                  chartDimensionsForGranularity(granularity),
                  config.chartMetrics,
                )
              }
              onGranularityChange={(chartGranularity) => {
                setConfig((current) => ({ ...current, chartGranularity }));
              }}
              onEditMetrics={() => {
                setChartMetricsOpen(true);
              }}
              onTimeShiftApply={(timeShiftDays) => {
                setConfig((current) => ({ ...current, timeShiftDays }));
              }}
              onTimeShiftClear={() => {
                setConfig((current) => ({ ...current, timeShiftDays: null }));
              }}
            />
            <ReportResultsTable
              request={ranTableRequest ?? tableRequest}
              response={ranTableRequest ? tableQuery.data : undefined}
              loading={ranTableRequest !== null && tableQuery.isLoading}
              error={ranTableRequest !== null && tableQuery.isError}
              notRun={ranTableRequest === null}
            />
          </div>
        </div>
      </div>
      {chartMetricsOpen
        ? renderChartMetricsDialog(config, availableFeatures, dashboardContext, setConfig, () => {
            setChartMetricsOpen(false);
          })
        : null}
      {saveOpen ? (
        <SaveReportDialog
          agencyId={agencyScope ? agencyId : undefined}
          initialName={saved?.name ?? ''}
          initialSharedWith={saved ? savedReportSharedUserIds(saved) : []}
          pending={mutations.create.isPending || mutations.update.isPending}
          onSubmit={saveReport}
          onClose={() => {
            setSaveOpen(false);
          }}
        />
      ) : null}
    </main>
  );
}
